import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

interface EmailPreferences {
  weekly_digest: boolean
  reminder_emails: boolean
  last_chance_emails: boolean
}

export default function EmailPreferences() {
  const [preferences, setPreferences] = useState<EmailPreferences>({
    weekly_digest: true,
    reminder_emails: true,
    last_chance_emails: true
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadPreferences()
  }, [])

  async function loadPreferences() {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      const { data, error } = await supabase
        .from('email_preferences')
        .select('*')
        .eq('user_id', session.user.id)
        .single()

      if (error) throw error

      if (data) {
        setPreferences({
          weekly_digest: data.weekly_digest,
          reminder_emails: data.reminder_emails,
          last_chance_emails: data.last_chance_emails
        })
      }
    } catch (error) {
      console.error('Error loading email preferences:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePreferenceChange = async (key: keyof EmailPreferences, value: boolean) => {
    setSaving(true)
    const newPreferences = { ...preferences, [key]: value }
    setPreferences(newPreferences)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      // First try to update
      let { error: updateError } = await supabase
        .from('email_preferences')
        .update({
          ...newPreferences,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', session.user.id)

      // If no rows were updated, then insert
      if (updateError && updateError.code === 'PGRST116') {
        const { error: insertError } = await supabase
          .from('email_preferences')
          .insert({
            user_id: session.user.id,
            ...newPreferences,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })

        if (insertError) throw insertError
      } else if (updateError) {
        throw updateError
      }
    } catch (error) {
      console.error('Error saving email preferences:', error)
      // Revert the change on error
      setPreferences(preferences)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="animate-pulse">Loading preferences...</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="weekly_digest"
          checked={preferences.weekly_digest}
          onChange={(e) => handlePreferenceChange('weekly_digest', e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
        />
        <div>
          <label htmlFor="weekly_digest" className="text-sm font-medium text-gray-900 cursor-pointer">
            Weekly Digest
          </label>
          <p className="text-xs text-gray-500">
            Receive a weekly summary of upcoming events (14-10 days before)
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="reminder_emails"
          checked={preferences.reminder_emails}
          onChange={(e) => handlePreferenceChange('reminder_emails', e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
        />
        <div>
          <label htmlFor="reminder_emails" className="text-sm font-medium text-gray-900 cursor-pointer">
            Reminder Emails
          </label>
          <p className="text-xs text-gray-500">
            Get reminders when events are approaching (10-7 days before)
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="last_chance_emails"
          checked={preferences.last_chance_emails}
          onChange={(e) => handlePreferenceChange('last_chance_emails', e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
        />
        <div>
          <label htmlFor="last_chance_emails" className="text-sm font-medium text-gray-900 cursor-pointer">
            Last Chance Alerts
          </label>
          <p className="text-xs text-gray-500">
            Receive urgent reminders for final send-by dates (7 days or less before)
          </p>
        </div>
      </div>

      {saving && (
        <p className="text-sm text-gray-500 italic">
          Saving preferences...
        </p>
      )}
    </div>
  )
} 